import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { SignInPage, SignOutPage } from "@codelittinc/carbon-design-system";
import { browserSessionState } from "../lib/session";
import { signOut } from "../lib/signin";

type Status = "loading" | "ready" | "pending" | "unavailable" | "signed-out";
const navigateDocument = (url: string) => window.location.assign(url);

/** What the sign-in page says after the server refused an account Gatekeeper does not know. */
export const REFUSED =
  "That account isn't in Carbon's staff directory, so it can't sign in to Carbon OS. Sign in with your work account, or raise a ticket with IT.";

/** Only return to workspace routes on this origin, never back into an auth loop. */
export function authReturn(search: string, origin: string): string {
  const raw = new URLSearchParams(search).get("return") ?? "/";
  try {
    const url = new URL(raw, origin);
    if (url.origin === origin && (url.pathname === "/" || url.pathname === "/training")) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch { /* Fall back to the workspace. */ }
  return "/";
}

export function AuthPage({ mode, navigate = navigateDocument }: {
  mode: "sign-in" | "sign-out";
  navigate?: (url: string) => void;
}) {
  const location = useLocation();
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    setError(null);
    document.title = `${mode === "sign-in" ? "Sign in" : "Sign out"} · Carbon OS`;
    void (async () => {
      try {
        let next: Status;
        if (mode === "sign-out") {
          const signedIn = await browserSessionState();
          next = signedIn === null ? "unavailable" : signedIn ? "ready" : "signed-out";
        } else {
          const res = await fetch("/api/session", { credentials: "same-origin", cache: "no-store" });
          if (!res.ok) throw new Error("Session unavailable");
          const body = await res.json();
          next = typeof body.clerkPublishableKey === "string" && body.clerkPublishableKey ? "ready" : "unavailable";
        }
        if (active) setStatus(next);
      } catch {
        if (active) setStatus("unavailable");
      }
    })();
    return () => { active = false; };
  }, [mode, location.pathname]);

  if (mode === "sign-in") {
    // Set by the server after it refused somebody Gatekeeper does not know.
    const refused = new URLSearchParams(location.search).has("refused");
    return <SignInPage
      appName="Carbon OS"
      status={status === "signed-out" ? "ready" : status}
      error={refused ? REFUSED : null}
      onSignIn={() => {
        setStatus("pending");
        const target = authReturn(location.search, window.location.origin);
        navigate(`/auth/session?return=${encodeURIComponent(target)}`);
      }}
    />;
  }

  return <SignOutPage
    appName="Carbon OS"
    status={status}
    error={error}
    onCancel={() => navigate("/")}
    onSignIn={() => navigate("/sign-in")}
    onSignOut={() => {
      setStatus("pending");
      setError(null);
      void signOut().catch(() => {
        setError("We couldn't sign you out. Please try again.");
        setStatus("ready");
      });
    }}
  />;
}
